import { z } from "zod";
//#region src/onboarding/sign-in-types.ts
/**
* The sign-in types that a tenant onboarding request can choose, and the fields of each.
*
* Plan `2026-10-16-customer-onboarding`, workstream WS-1, decisions D12, D17, D20 and D23.
*
* Who uses it:
*
* - provisioning-svc (WS-6) binds {@link signInInputSchema} inside the body of
*   `POST /v1/onboarding-requests`, and stores only {@link signInViewSchema} (D12: the CP database
*   never stores a client secret).
* - release-engine (WS-8) copies the view into the org seed of the manifest
*   (`@zion/sdk-cp-dp-contracts`, `orgSeedSchema`).
* - core-api (WS-9, WS-14) reads the type and the non-secret fields from the seed.
* - The console wizard (WS-16), site-admin-api (WS-17) and `zion-ops` (WS-18) render the fields
*   from {@link SIGN_IN_TYPE_SPECS}.
*
* The six types are the four types of the core-api catalog (`password`, `passkey`, `google`,
* `github`) and the two enterprise types this feature adds (`oidc` for Okta, `workos`). The intake
* offers five of them, {@link SIGN_IN_TYPES_OFFERED}: every door refuses `workos` (R-063 item 3,
* R-066 item 1, R-070 item 2).
*
* The client secret can follow the intake (R-055, R-070 item 1). A `google`, `github` or `oidc`
* choice carries exactly one of `clientSecret` or the marker `clientSecretLater: true`. The marker
* stays in the stored request and in the view. provisioning-svc then waits in
* `awaiting_client_secret` until an operator sends {@link onboardingClientSecretBodySchema}.
*
* Every schema here is strict and has refinements and no transform, so `createOpenApiDocument()`
* can represent it. Every function is pure: it reads no clock, no network and no setting.
*/
/** The sign-in types, in the order a form lists them. */
const SIGN_IN_TYPES = [
	"password",
	"passkey",
	"google",
	"github",
	"oidc",
	"workos"
];
/**
* The sign-in types that an onboarding request may choose, in the order a form lists them:
* {@link SIGN_IN_TYPES} without `workos` (R-070 item 3).
*
* Who reads it: the console wizard (WS-16) for its type choice, the MCP tool `onboard_tenant`
* (WS-17) for its argument enum, and `zion-ops tenant onboard` (WS-18) for its flag values.
* `onboardingRequestSchema` refuses a type outside this list. `workos` stays in
* {@link SIGN_IN_TYPES}, {@link signInInputSchema} and {@link signInViewSchema}: no email-trust
* ruling exists for it, so every door refuses it, and the unions keep it so that one later ruling
* opens it in one place (R-063 item 3).
*/
const SIGN_IN_TYPES_OFFERED = [
	"password",
	"passkey",
	"google",
	"github",
	"oidc"
];
/** Runtime validator for {@link SIGN_IN_TYPES}. Each member parses to itself; any other value is a `ZodError`. */
const signInTypeSchema = z.enum(SIGN_IN_TYPES);
/**
* The categories of the core-api catalog (`ProviderTypeDefinition.category`) and of
* `IdpProviderCatalogEntry.category`. `builtin` runs no OAuth flow, `oauth` runs a flow against a
* fixed vendor, and `enterprise` runs a flow against an IdP the tenant names.
*/
const SIGN_IN_CATEGORIES = [
	"builtin",
	"oauth",
	"enterprise"
];
const CLIENT_ID_FIELD = {
	key: "clientId",
	required: true,
	isSecret: false
};
const CLIENT_SECRET_FIELD = {
	key: "clientSecret",
	required: true,
	isSecret: true
};
/**
* The spec of every sign-in type, keyed by type.
*
* - `password` and `passkey`: no field.
* - `google`: `clientId` and `clientSecret`.
* - `github`: `clientId`, `clientSecret` and the optional `orgName`.
* - `oidc`: `issuer`, `clientId` and `clientSecret`. The scopes are a constant of the core-api
*   catalog entry (WS-14), so the request carries none (default F1, the narrower type).
* - `workos`: `organizationId` only. The WorkOS client id and secret belong to one WorkOS
*   environment of the platform, so they are a core-api setting and never a tenant field
*   (research 11 section 4).
*
* The display names of `password`, `passkey`, `google` and `github` are the `displayName` values of
* the core-api catalog `PROVIDER_CATALOG`.
*/
const SIGN_IN_TYPE_SPECS = Object.freeze({
	password: {
		type: "password",
		displayName: "Password",
		category: "builtin",
		fields: []
	},
	passkey: {
		type: "passkey",
		displayName: "Passkeys",
		category: "builtin",
		fields: []
	},
	google: {
		type: "google",
		displayName: "Google SSO",
		category: "oauth",
		fields: [CLIENT_ID_FIELD, CLIENT_SECRET_FIELD]
	},
	github: {
		type: "github",
		displayName: "GitHub SSO",
		category: "oauth",
		fields: [
			CLIENT_ID_FIELD,
			CLIENT_SECRET_FIELD,
			{
				key: "orgName",
				required: false,
				isSecret: false
			}
		]
	},
	oidc: {
		type: "oidc",
		displayName: "OpenID Connect",
		category: "enterprise",
		fields: [
			{
				key: "issuer",
				required: true,
				isSecret: false
			},
			CLIENT_ID_FIELD,
			CLIENT_SECRET_FIELD
		]
	},
	workos: {
		type: "workos",
		displayName: "WorkOS SSO",
		category: "enterprise",
		fields: [{
			key: "organizationId",
			required: true,
			isSecret: false
		}]
	}
});
/**
* Tell whether a sign-in type takes a client secret at intake.
*
* Returns `true` for `google`, `github` and `oidc`, and `false` for `password`, `passkey` and
* `workos`. provisioning-svc reads it to decide whether the job writes a secret to the data plane
* (D12), and whether its client secret route accepts a paste (R-071 item 2). The answer is the
* `isSecret` field of {@link SIGN_IN_TYPE_SPECS}. These are also the three types that may carry
* the marker `clientSecretLater: true`.
*/
function signInTypeTakesClientSecret(type) {
	return SIGN_IN_TYPE_SPECS[type].fields.some((field) => field.isSecret);
}
/** The longest `clientId`, in characters. */
const SIGN_IN_CLIENT_ID_MAX_LENGTH = 256;
/** The longest `clientSecret`, in characters. */
const SIGN_IN_CLIENT_SECRET_MAX_LENGTH = 512;
/**
* The text of a `clientId` or a `clientSecret`: visible ASCII characters (0x21 to 0x7e), so no
* space, no control character and no non-ASCII character. Every client id and secret of Google,
* GitHub, Okta and WorkOS is in this set.
*/
const SIGN_IN_CREDENTIAL_PATTERN = /^[\x21-\x7e]+$/;
/**
* The suffix of every Google OAuth client id. The core-api `testProvider` check of the setup wizard
* refuses a Google client id without it, and this contract refuses it at intake too (default F3).
*/
const GOOGLE_CLIENT_ID_SUFFIX = ".apps.googleusercontent.com";
/**
* A GitHub organization login: letters, digits and single inner hyphens, 1 to 39 characters.
*
* The same language as `GITHUB_ORGANIZATION_LOGIN` in the core-api `provider-catalog.ts`, which
* refuses a stored `orgName` that does not match at sign-in. This copy refuses it at intake. A
* contract package cannot import a service, so core-api keeps its own copy until it imports this
* one.
*/
const GITHUB_ORGANIZATION_LOGIN_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9]|-(?=[A-Za-z0-9])){0,38}$/;
/**
* A WorkOS organization id: `org_` and 26 characters of Crockford base32 in upper case (a ULID).
* The pattern comes from the WorkOS id format, not from a live WorkOS account.
*/
const WORKOS_ORGANIZATION_ID_PATTERN = /^org_[0-9A-HJKMNP-TV-Z]{26}$/;
/** The longest OIDC `issuer`, in characters. */
const OIDC_ISSUER_MAX_LENGTH = 512;
/**
* The last host labels that name no public host. An issuer under one of them would make the data
* plane fetch a discovery document from inside its own network.
*/
const OIDC_ISSUER_REFUSED_TOP_LABELS = /* @__PURE__ */ new Set([
	"localhost",
	"local",
	"localdomain",
	"internal",
	"intranet",
	"lan",
	"home",
	"corp",
	"private",
	"arpa",
	"svc"
]);
/**
* Check an OIDC issuer before any code fetches its discovery document.
*
* Callers: {@link signInInputSchema} through its refinement, and core-api (WS-14) before it stores
* or fetches an issuer. This check reads the text only. The DNS answer of the host can still name a
* private address, so the core-api fetch keeps its own public-address guard (overview section 8).
*
* Input: any string. Output: `null` for an issuer that has the `https` scheme, no port, a public
* host name with a dot, a path or no path, and the exact form the URL parser prints with no
* trailing slash. A refusal reason of {@link OidcIssuerRefusal} for every other string. It never
* throws.
*/
function oidcIssuerRefusal(value) {
	let url;
	try {
		url = new URL(value);
	} catch {
		return "not-a-url";
	}
	if (url.protocol !== "https:") return "not-https";
	if (url.port !== "") return "has-port";
	if ((url.pathname === "/" ? url.origin : `${url.origin}${url.pathname}`) !== value || value.endsWith("/")) return "not-canonical";
	const host = url.hostname;
	const labels = host.split(".");
	const topLabel = host.slice(host.lastIndexOf(".") + 1);
	return host.startsWith("[") || /^[0-9.]+$/.test(host) || labels.length < 2 || labels.includes("") || OIDC_ISSUER_REFUSED_TOP_LABELS.has(topLabel) ? "host-not-public" : null;
}
/** The message of a refused OIDC issuer. It names the rule, not the submitted value. */
const OIDC_ISSUER_MESSAGE = "issuer must be an https URL on a public host name, with no port, user information, query, fragment or trailing slash";
const clientIdSchema = z.string().min(1).max(256).regex(SIGN_IN_CREDENTIAL_PATTERN, "clientId must hold visible ASCII characters only");
const googleClientIdSchema = clientIdSchema.refine((value) => value.endsWith(GOOGLE_CLIENT_ID_SUFFIX), { message: `a Google clientId ends in ${GOOGLE_CLIENT_ID_SUFFIX}` });
const clientSecretSchema = z.string().min(1).max(512).regex(SIGN_IN_CREDENTIAL_PATTERN, "clientSecret must hold visible ASCII characters only");
const issuerSchema = z.string().max(512).refine((value) => oidcIssuerRefusal(value) === null, { message: OIDC_ISSUER_MESSAGE });
const orgNameSchema = z.string().regex(GITHUB_ORGANIZATION_LOGIN_PATTERN, "orgName must be a GitHub organization login");
const organizationIdSchema = z.string().regex(WORKOS_ORGANIZATION_ID_PATTERN, "organizationId must be a WorkOS organization id");
/**
* The message of a `google`, `github` or `oidc` choice that carries neither or both of
* `clientSecret` and `clientSecretLater: true` (R-055 item 1). It names the rule, not a value.
*/
const SIGN_IN_CLIENT_SECRET_CHOICE_MESSAGE = "send exactly one of clientSecret or clientSecretLater: true";
/** The marker of a client secret that follows the intake: the one value `true` (R-055 item 1). */
const clientSecretLaterSchema = z.literal(true);
/**
* The exactly-one rule of a secret member. Neither key names `clientSecret`, the field a form
* fills; both keys name `clientSecretLater`, the key to remove. A type failure of another field
* aborts the member, and then the rule does not run.
*/
function exactlyOneSecretChoice(member, ctx) {
	const hasSecret = member.clientSecret !== void 0;
	if (hasSecret === (member.clientSecretLater !== void 0)) ctx.addIssue({
		code: "custom",
		path: [hasSecret ? "clientSecretLater" : "clientSecret"],
		message: SIGN_IN_CLIENT_SECRET_CHOICE_MESSAGE
	});
}
const passwordSchema = z.strictObject({ type: z.literal("password") });
const passkeySchema = z.strictObject({ type: z.literal("passkey") });
const workosSchema = z.strictObject({
	type: z.literal("workos"),
	organizationId: organizationIdSchema
});
/**
* The sign-in choice of an onboarding request, as the caller sends it.
*
* A union on `type`, each member strict:
* - `{ type: 'password' }` and `{ type: 'passkey' }`;
* - `{ type: 'google', clientId, clientSecret | clientSecretLater }`, where `clientId` ends in
*   {@link GOOGLE_CLIENT_ID_SUFFIX};
* - `{ type: 'github', clientId, clientSecret | clientSecretLater, orgName? }`;
* - `{ type: 'oidc', issuer, clientId, clientSecret | clientSecretLater }`, where `issuer` passes
*   {@link oidcIssuerRefusal};
* - `{ type: 'workos', organizationId }`.
*
* `clientId` and `clientSecret` are 1 to 256 and 1 to 512 visible ASCII characters.
* `clientSecretLater` is the literal `true`. A `google`, `github` or `oidc` member carries exactly
* one of `clientSecret` and `clientSecretLater` (R-055 item 1). Neither is an issue at
* `clientSecret`, and both is an issue at `clientSecretLater`, each with
* {@link SIGN_IN_CLIENT_SECRET_CHOICE_MESSAGE}. A key that the type does not declare is refused, so
* a `clientSecret` or a `clientSecretLater` on `password` or `workos` is a 400 and not a dropped
* key. The parse returns the same object shape it read.
*
* This union accepts `workos`. `onboardingRequestSchema` refuses it at intake (R-070 item 2).
*/
const signInInputSchema = z.discriminatedUnion("type", [
	passwordSchema,
	passkeySchema,
	z.strictObject({
		type: z.literal("google"),
		clientId: googleClientIdSchema,
		clientSecret: clientSecretSchema.optional(),
		clientSecretLater: clientSecretLaterSchema.optional()
	}).superRefine(exactlyOneSecretChoice),
	z.strictObject({
		type: z.literal("github"),
		clientId: clientIdSchema,
		clientSecret: clientSecretSchema.optional(),
		clientSecretLater: clientSecretLaterSchema.optional(),
		orgName: orgNameSchema.optional()
	}).superRefine(exactlyOneSecretChoice),
	z.strictObject({
		type: z.literal("oidc"),
		issuer: issuerSchema,
		clientId: clientIdSchema,
		clientSecret: clientSecretSchema.optional(),
		clientSecretLater: clientSecretLaterSchema.optional()
	}).superRefine(exactlyOneSecretChoice),
	workosSchema
]);
/**
* The sign-in choice without its secret: the form the CP stores, returns and puts in the org seed.
*
* The same union as {@link signInInputSchema} with no `clientSecret` member. Each member is strict,
* so a stored row or a response that still carries `clientSecret` fails this parse: a reader
* refuses a leaked secret and does not drop it in silence (default F8).
*
* A `google`, `github` or `oidc` view keeps the optional marker `clientSecretLater: true` when the
* input had it (R-055 item 1). The marker tells provisioning-svc to wait for the secret, and tells
* the console to show the paste panel. The org seed drops it (`orgSeedFromRecord`, R-070 item 5).
*/
const signInViewSchema = z.discriminatedUnion("type", [
	passwordSchema,
	passkeySchema,
	z.strictObject({
		type: z.literal("google"),
		clientId: googleClientIdSchema,
		clientSecretLater: clientSecretLaterSchema.optional()
	}),
	z.strictObject({
		type: z.literal("github"),
		clientId: clientIdSchema,
		orgName: orgNameSchema.optional(),
		clientSecretLater: clientSecretLaterSchema.optional()
	}),
	z.strictObject({
		type: z.literal("oidc"),
		issuer: issuerSchema,
		clientId: clientIdSchema,
		clientSecretLater: clientSecretLaterSchema.optional()
	}),
	workosSchema
]);
/**
* Remove the client secret from a sign-in choice.
*
* Caller: provisioning-svc, before it writes the request row (D12), and every code path that
* returns or logs a request. Input: a parsed {@link SignInInput}. Output: a new
* {@link SignInView} that names each non-secret field of the type and nothing else; `orgName`
* appears only when the input has it, and `clientSecretLater: true` only when the input has the
* marker (R-070 item 1). The input object is not changed.
*/
function withoutClientSecret(input) {
	switch (input.type) {
		case "google": return {
			type: "google",
			clientId: input.clientId,
			...laterMarkerOf(input)
		};
		case "github": return {
			type: "github",
			clientId: input.clientId,
			...input.orgName === void 0 ? {} : { orgName: input.orgName },
			...laterMarkerOf(input)
		};
		case "oidc": return {
			type: "oidc",
			issuer: input.issuer,
			clientId: input.clientId,
			...laterMarkerOf(input)
		};
		case "workos": return {
			type: "workos",
			organizationId: input.organizationId
		};
		case "password":
		case "passkey": return { type: input.type };
	}
}
/** The marker part of a view: `{ clientSecretLater: true }` when the choice has the marker, else `{}`. */
function laterMarkerOf(member) {
	return member.clientSecretLater === true ? { clientSecretLater: true } : {};
}
/**
* Read the client secret of a sign-in choice.
*
* Caller: provisioning-svc, which writes the value once to the data plane secret path (D12) and
* keeps it nowhere else. Output: the `clientSecret` for `google`, `github` and `oidc`, and `null`
* for `password`, `passkey` and `workos`. A choice with the marker `clientSecretLater: true`
* answers `null`, also when an unparsed object carries both keys: the marker wins, so the job
* waits for the paste and holds no value (R-070 item 1).
*/
function clientSecretOf(input) {
	if (input.type !== "google" && input.type !== "github" && input.type !== "oidc") return null;
	return input.clientSecretLater === true ? null : input.clientSecret ?? null;
}
/**
* The body of `PUT /v1/onboarding-requests/:id/client-secret` (R-055 item 3, R-070 item 4):
* `{ clientSecret }`, strict, 1 to 512 visible ASCII characters, the same rule as the
* `clientSecret` of {@link signInInputSchema}.
*
* Callers: provisioning-svc binds it on the route, site-admin-api (WS-17) binds it on its proxy
* route, and the console paste panel (WS-16) and `zion-ops tenant onboard-secret` (WS-18) build
* it. The route answers 204 with no body. No log, answer or audit row carries the value (F8).
* An unknown key, a missing key or a broken rule is a 400 from the pipe, and the issue message
* names the rule and never the value.
*/
const onboardingClientSecretBodySchema = z.strictObject({ clientSecret: clientSecretSchema });
//#endregion
export { GITHUB_ORGANIZATION_LOGIN_PATTERN, GOOGLE_CLIENT_ID_SUFFIX, OIDC_ISSUER_MAX_LENGTH, OIDC_ISSUER_MESSAGE, OIDC_ISSUER_REFUSED_TOP_LABELS, SIGN_IN_CATEGORIES, SIGN_IN_CLIENT_ID_MAX_LENGTH, SIGN_IN_CLIENT_SECRET_CHOICE_MESSAGE, SIGN_IN_CLIENT_SECRET_MAX_LENGTH, SIGN_IN_CREDENTIAL_PATTERN, SIGN_IN_TYPES, SIGN_IN_TYPES_OFFERED, SIGN_IN_TYPE_SPECS, WORKOS_ORGANIZATION_ID_PATTERN, clientSecretOf, oidcIssuerRefusal, onboardingClientSecretBodySchema, signInInputSchema, signInTypeSchema, signInTypeTakesClientSecret, signInViewSchema, withoutClientSecret };

//# sourceMappingURL=sign-in-types.js.map